import { getRouteApi } from '@tanstack/react-router';
import { Row } from '@tanstack/react-table';
import { record } from 'fp-ts';
import { pipe } from 'fp-ts/function';
import { Ord } from 'fp-ts/string';
import {
    OnboardingAuditArea,
    OnboardingAuditField,
    OnboardingAuditResult,
    OnboardingAuditStatus,
    useRepositorySuspenseQuery,
} from '@mews-commander/data-access';
import { ChartData, ProgressChart } from '@mews/b2b-ui';
import { LocalizedEnum, LocalizedText, lt, useFeatureFlag } from '@mews/framework';
import { TextKey, FeatureFlag } from '@mews-commander/enums';
import {
    Button,
    Container,
    GroupingTableCell,
    IconName,
    SegmentedControl,
    SegmentedControlItem,
    SegmentedControlProps,
    Spinner,
    Stack,
    Table,
    TableHeader,
    TableProvider,
    Typography,
    useTableState,
} from '@optimus-web/core';
import { SectionStatusTag } from './AuditStatusTag';
import { auditMatchesStatusFilter, collateStatusFromFields, groupAudits } from './auditTransforms';
import { getColumns } from './columns';
import { AuditStatusFilter } from './routeSchemas';
import { useUpdateActivityLog } from './activityLog';
import { usePrivilegeUserCanEdit } from './hooks';
import {
    compareFinancialAndSpacesOrder,
    isFinancialAndSpaces,
    isGuestExperience,
    isRevenueManagement,
    isServiceOrProduct,
} from './utils';

interface GoLiveTaskTableProps {
    audits: Array<OnboardingAuditField>;
    inDialog?: boolean;
    title?: TextKey;
}

export const GoLiveTaskTable = ({ inDialog, audits, title }: GoLiveTaskTableProps) => {
    const [tableState, setTableState] = useTableState<OnboardingAuditField>({
        pagination: { show: false },
        grouping: ['area'],
    });

    const getRowGroupLabel = (row: Row<OnboardingAuditField>) => (
        <Typography textStyle="bodyMediumStrong" as="div">
            <LocalizedEnum prefix="OnboardingAuditArea" value={row.groupingValue as OnboardingAuditArea} />
        </Typography>
    );

    const getRowGroupContent = (row: Row<OnboardingAuditField>) => {
        const areaStatus = collateStatusFromFields(row);
        const totalTasks = row.subRows.length;

        const completedTasks = row.subRows.filter(
            (subRow) =>
                subRow.original.status === OnboardingAuditStatus.Completed ||
                subRow.original.status === OnboardingAuditStatus.NotApplicable
        ).length;

        const progressFraction = `${completedTasks} / ${totalTasks}`;

        const areaProgressData: ChartData[] = [
            {
                id: row.groupingValue?.toString() || '',
                name: null,
                value: completedTasks,
                color: 'blue',
            },
        ];

        return (
            <>
                <GroupingTableCell>
                    <SectionStatusTag status={areaStatus} />
                </GroupingTableCell>
                <GroupingTableCell colSpan={3} align="end">
                    <Stack spacing="50" distribution="space-between" itemAlignment="center">
                        <ProgressChart minWidth="75px" total={totalTasks} data={areaProgressData} />
                        <Container width="50px">
                            <Stack alignment="end">{progressFraction}</Stack>
                        </Container>
                    </Stack>
                </GroupingTableCell>
            </>
        );
    };

    return (
        <TableProvider columns={getColumns(inDialog)} state={tableState} onStateChange={setTableState}>
            <Stack vertical>
                {title ? <TableHeader title={lt(title)} /> : null}
                <Table
                    data={audits}
                    amountOfRows={audits.length}
                    uniqueRowId="fieldIdentifier"
                    fixedFirstColumn
                    columnVisibility={{ area: false }}
                    getRowGroupComponent={getRowGroupContent}
                    getRowGroupLabel={getRowGroupLabel}
                    groupedRowColSpan={1}
                />
            </Stack>
        </TableProvider>
    );
};

/** Creates the selector for react-query that groups the audits ready for use in the grouped tables  */
const createAuditSelect =
    (auditStatus: AuditStatusFilter) =>
    ({
        legalEnvironmentData,
        kycStatus,
        ...auditChecks
    }: OnboardingAuditResult): {
        auditList: OnboardingAuditField[];
        groupedAudits: OnboardingAuditField[];
    } => {
        const groupedAudits = pipe(
            auditChecks,
            record.filter(
                (audit: OnboardingAuditField) =>
                    auditMatchesStatusFilter(audit, auditStatus) && audit.area !== OnboardingAuditArea.Marketplace
            ),
            groupAudits
        );

        const auditList = pipe(
            auditChecks,
            record.filter((audit: OnboardingAuditField) => audit.area !== OnboardingAuditArea.Marketplace),
            record.collect(Ord)((_key, audit) => audit)
        );

        return { auditList, groupedAudits };
    };

const SetupRoute = getRouteApi('/_authenticated/$currentEnterpriseId/Settings/GoLiveAudits/Setup');

export const GoLiveTasksContainer = () => {
    const { currentEnterpriseId } = SetupRoute.useParams();
    const navigate = SetupRoute.useNavigate();
    const { auditStatus } = SetupRoute.useSearch();
    const onboardingAuditLogEnabled = useFeatureFlag(FeatureFlag.onboardingAuditLogEnabled);
    const onboardingAuditStatusFilterEnabled = useFeatureFlag(FeatureFlag.onboardingAuditStatusFilterEnabled);
    const userCanEdit = usePrivilegeUserCanEdit();

    const {
        data: { auditList, groupedAudits },
        isRefetching,
    } = useRepositorySuspenseQuery(
        'enterprises',
        'getOnboardingAudit',
        { enterpriseId: currentEnterpriseId },
        {
            refetchOnWindowFocus: 'always',
            select: createAuditSelect(auditStatus),
        }
    );

    const [renderUpdateActivityLogDialog, toggleUpdateActivityLogDialog] = useUpdateActivityLog({ auditList });

    const handleFilterChange: SegmentedControlProps['onChange'] = (_e, value) =>
        void navigate({
            // @ts-expect-error due to router types not being available w/out circular references
            search: (params) => ({ ...params, auditStatus: value }),
        });

    const financialAndSpaces = groupedAudits
        .filter((audit) => isFinancialAndSpaces(audit.area))
        .sort((a, b) => compareFinancialAndSpacesOrder(a.area, b.area));

    const servicesAndProducts = groupedAudits.filter((audit) => isServiceOrProduct(audit.area));
    const revenueManagement = groupedAudits.filter((audit) => isRevenueManagement(audit.area));
    const guestExperience = groupedAudits.filter((audit) => isGuestExperience(audit.area));

    return (
        <Container padding={['300', '0', '0', '0']}>
            <Stack vertical>
                <Stack spacing="0" itemAlignment="end" alignment="end">
                    {onboardingAuditLogEnabled && userCanEdit ? (
                        <>
                            <Button
                                icon={IconName.ActionLog}
                                size="small"
                                variant="tertiary"
                                type="button"
                                onClick={toggleUpdateActivityLogDialog}
                            >
                                <LocalizedText textKey={TextKey.GoLiveAuditsUpdateActivityLog} />
                            </Button>
                            {renderUpdateActivityLogDialog()}
                        </>
                    ) : null}
                </Stack>
                {onboardingAuditStatusFilterEnabled ? (
                    <Stack alignment="start" itemAlignment="start" distribution="stretch" spacing="50">
                        <Container desktopUp={{ maxWidth: '55%' }} largeDesktopOnly={{ maxWidth: '35%' }}>
                            <SegmentedControl value={auditStatus} onChange={handleFilterChange} size="small">
                                <SegmentedControlItem
                                    label={<LocalizedText textKey={TextKey.GoLiveAuditsPageFilterPending} />}
                                    value={AuditStatusFilter.Pending}
                                />
                                <SegmentedControlItem
                                    label={<LocalizedText textKey={TextKey.GoLiveAuditsPageFilterFailed} />}
                                    value={AuditStatusFilter.Failed}
                                />
                                <SegmentedControlItem
                                    label={<LocalizedText textKey={TextKey.GoLiveAuditsPageFilterCompleted} />}
                                    value={AuditStatusFilter.Completed}
                                />
                                <SegmentedControlItem
                                    label={<LocalizedText textKey={TextKey.GoLiveAuditsPageFilterAll} />}
                                    value={AuditStatusFilter.All}
                                />
                            </SegmentedControl>
                        </Container>
                        <Stack alignment="end">{isRefetching ? <Spinner size="small" /> : null}</Stack>
                    </Stack>
                ) : null}
                <Stack vertical spacing="400">
                    <GoLiveTaskTable
                        audits={financialAndSpaces}
                        title={TextKey.OnboardingAuditFinancialSpacesTableHeader}
                    />
                    <GoLiveTaskTable
                        audits={servicesAndProducts}
                        title={TextKey.OnboardingAuditServicesAndProductsTableHeader}
                    />
                    <GoLiveTaskTable
                        audits={revenueManagement}
                        title={TextKey.OnboardingAuditRevenueManagementTableHeader}
                    />
                    <GoLiveTaskTable
                        audits={guestExperience}
                        title={TextKey.OnboardingAuditGuestExperienceTableHeader}
                    />
                </Stack>
            </Stack>
        </Container>
    );
};
